/*
 * @Copyright 2023-2024. Institute for Future Intelligence, Inc.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Matrix4, Vector3 } from 'three';
import { useStore } from '../../stores/common';
import * as Selector from '../../stores/selector';
import { useLanguage } from '../../hooks';
import { Button, Checkbox, InputNumber, Popover, Space } from 'antd';
import { MainMenuItem, MainSubMenu, stopMenuKeyboardPropagation } from '../menuItem';
import { runWithUndo, setWithUndo } from '../../undo/setWithUndo.ts';
import { pasteMoleculeWithUndo } from '../../undo/pasteMolecule';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { useTranslation } from 'react-i18next';
import { usePrimitiveStore } from '../../stores/commonPrimitive';
import { RotateLeftOutlined, RotateRightOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import {
  INDIVIDUAL_MOLECULE_STYLE_LABELS,
  MolecularViewerStyle,
  OVERLAY_STYLE_LABELS,
} from '../../view/displayOptions';
import { useRefStore } from '../../stores/commonRef.ts';
import { Util } from '../../Util.ts';
import { Molecule } from '../../models/Molecule.ts';
import { cloneLabMoleculeLive } from '../../undo/liveMoleculeClone.ts';
import { useDataStore } from '../../stores/commonData.ts';
import { isCartoon } from '../../view/moleculeTools.ts';
import { Restraint } from '../../models/Restraint.ts';
import { EV_CONVERTER } from '../../models/physicalConstants.ts';
import { UndoableDeleteMoleculeInChamber } from '../../undo/UndoableDelete.ts';
import { cutMoleculeWithUndo } from '../../undo/cutMolecule.ts';
import { UndoableMoleculeRotation, UndoableMoleculeTranslation } from '../../undo/UndoableMove.ts';
import { ClickEvent, MenuItem, MenuRadioGroup, RadioChangeEvent } from '@szhsin/react-menu';
import {
  blockedWhileSimulating,
  copyRestraintAnchorToStore,
  markEngineDirty,
  transformRestraintAnchor,
} from '../../view/engineSync.ts';

// Every handler below edits the LIVE atoms, which the main-thread mirror engine
// shares — but with the simulation worker that mirror is a display copy. The
// worker only learns of an edit through markEngineDirty(): paused → a syncState
// (a move — markEngineDirty({ moved: true }) — also becomes the Reset target;
// a parameter edit does not), running → a parameter-
// only sync riding the next packet. Without it a mid-run edit is overwritten by
// the next packet (positions / velocities) or never reaches the integrator at
// all (fixed / charge / epsilon / damping / restraint) until the next pause.
// Position edits cannot ride a packet, so Translate / Rotate / Spark are
// disabled while running — the same rule the transform gizmo applies.

// Coalesce a numeric context-menu edit (charge, restraint, damping, epsilon)
// into ONE undoable. These fields apply their value live on every onChange for
// responsiveness, so wrapping each onChange in setWithUndo would spam the stack
// with an entry per increment — and originally they registered NO undoable at
// all, so Ctrl-Z couldn't reverse a mis-set value. Instead: snapshot the pre-
// edit value on focus, and on blur register a single old->new entry if it moved.
// apply() has already run live, so setWithUndo's re-apply is an idempotent no-op
// that just wires up undo/redo (and marks the project dirty).
// The entry is also committed when the field unmounts mid-edit: closing the menu
// with Esc or a click outside removes the field without a blur, and the edit —
// already applied — used to get no undo entry at all. `value` is the field's
// current value, tracked so the unmount commit records where it ended.
// Per-atom restraint strengths of one lab molecule (0 = none), for the
// Restraint field's undo entry. Strengths only, not anchors: putting a saved
// anchor back after the atoms moved (a drag or a run since) would stretch the
// spring by that whole distance — 10 Å at 5 eV/Å² is a 50 eV/Å kick that flings
// the atom. So undo/redo works like typing the value: an existing restraint
// keeps its anchor, a re-created one anchors where the atom is now (the same
// rule as the per-atom Restraint field).
type RestraintSnapshot = {
  index: number;
  name: string;
  strengths: number[];
};

const snapshotRestraints = (index: number): RestraintSnapshot | null => {
  const storeMol = useStore.getState().projectState.testMolecules[index];
  const mol = useRefStore.getState().moleculesRef?.current?.[index];
  if (!storeMol || !mol || storeMol.atoms.length !== mol.atoms.length) return null;
  return { index, name: storeMol.name, strengths: mol.atoms.map((a) => a.restraint?.strength ?? 0) };
};

// Applied only if the slot still holds the same molecule (name and atom count):
// a list edit since then would otherwise restrain a different molecule. Store
// and live atoms get separate Restraint objects, as in setRestraint.
const applyRestraintSnapshot = (snap: RestraintSnapshot) => {
  const storeMol = useStore.getState().projectState.testMolecules[snap.index];
  const mol = useRefStore.getState().moleculesRef?.current?.[snap.index];
  const n = snap.strengths.length;
  if (!storeMol || storeMol.name !== snap.name || storeMol.atoms.length !== n) return;
  const live = mol && mol.atoms.length === n ? mol : null;
  useStore.getState().set((state) => {
    const m = state.projectState.testMolecules[snap.index];
    if (!m) return;
    for (let i = 0; i < n; i++) {
      const s = snap.strengths[i];
      const a = m.atoms[i];
      if (s <= 0) a.restraint = undefined;
      else if (a.restraint) (a.restraint as Restraint).strength = s;
      else {
        const p = live ? live.atoms[i].position : a.position;
        a.restraint = new Restraint(s, new Vector3(p.x, p.y, p.z));
      }
    }
  });
  if (live) {
    for (let i = 0; i < n; i++) {
      const s = snap.strengths[i];
      const a = live.atoms[i];
      if (s <= 0) a.restraint = undefined;
      else if (a.restraint) a.restraint.strength = s;
      else a.restraint = new Restraint(s, a.position.clone());
    }
  }
  usePrimitiveStore.getState().setChanged(true);
  markEngineDirty();
};

const useNumericFieldUndo = (name: string, apply: (v: number) => void, value: number) => {
  const startRef = useRef<number | null>(null);
  const latestRef = useRef(value);
  latestRef.current = value;
  const commit = (current: number) => {
    const start = startRef.current;
    startRef.current = null;
    if (start === null || start === current) return;
    setWithUndo(name, start, current, apply);
  };
  const commitRef = useRef(commit);
  commitRef.current = commit;
  useEffect(() => () => commitRef.current(latestRef.current), []);
  return {
    begin: (current: number) => {
      startRef.current = current;
    },
    commit,
  };
};

export const TranslateMolecule = () => {
  const setCommonStore = useStore(Selector.set);
  const setChanged = usePrimitiveStore(Selector.setChanged);
  const loggable = useStore(Selector.loggable);
  const logAction = useStore(Selector.logAction);
  const addUndoable = useStore(Selector.addUndoable);
  const updateViewer = usePrimitiveStore(Selector.updateViewer);
  const pickedIndex = usePrimitiveStore(Selector.pickedMoleculeIndex);
  const pickedMoleculeIndices = usePrimitiveStore(Selector.pickedMoleculeIndices);
  const molecularContainer = useStore(Selector.molecularContainer);
  const startSimulation = usePrimitiveStore(Selector.startSimulation);
  const moleculesRef = useRefStore.getState().moleculesRef;

  const [updateFlag, setUpdateFlag] = useState<boolean>(false);

  // Working set of molecule indices. Fall back to the single-pick anchor
  // so the existing single-molecule UX is unchanged when nothing was
  // shift-selected.
  const targetIndices = useMemo(() => {
    if (pickedMoleculeIndices.size > 0) return Array.from(pickedMoleculeIndices);
    return pickedIndex !== -1 ? [pickedIndex] : [];
  }, [pickedMoleculeIndices, pickedIndex]);

  // Centroid across all selected molecules. For a single molecule this
  // reduces to its own center; for multi-select it's the average of the
  // per-molecule centers so dragging the group preserves relative
  // arrangement.
  const center = useMemo(() => {
    if (!moleculesRef?.current || targetIndices.length === 0) return new Vector3();
    const sum = new Vector3();
    let count = 0;
    for (const idx of targetIndices) {
      const m = moleculesRef.current[idx];
      if (!m) continue;
      m.updateCenter();
      sum.add(m.center);
      count++;
    }
    if (count === 0) return new Vector3();
    return sum.multiplyScalar(1 / count);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moleculesRef, targetIndices, updateFlag]);

  // Moves the given molecules (live + store) by `displacement`. Takes the indices
  // explicitly: the undo entry replays it after this menu has closed.
  const translateIndices = (indices: number[], displacement: Vector3) => {
    const liveMolecules = useRefStore.getState().moleculesRef?.current;
    if (!liveMolecules || indices.length === 0) return;
    for (const idx of indices) {
      const m = liveMolecules[idx];
      if (!m) continue;
      for (const a of m.atoms) {
        a.position.add(displacement);
        // A placed molecule is the new Reset target (as with the gizmo).
        a.initialPosition?.copy(a.position);
        // …and its restraint anchor (if any) rides along — see engineSync.
        transformRestraintAnchor(a, (anchor) => anchor.add(displacement));
      }
    }
    setCommonStore((state) => {
      for (const idx of indices) {
        const mol = state.projectState.testMolecules[idx];
        const live = liveMolecules[idx];
        if (!mol) continue;
        for (const [i, a] of mol.atoms.entries()) {
          a.position.x += displacement.x;
          a.position.y += displacement.y;
          a.position.z += displacement.z;
          if (live?.atoms[i]) copyRestraintAnchorToStore(a, live.atoms[i]);
        }
      }
    });
    updateViewer();
    setChanged(true);
    markEngineDirty({ moved: true });
  };

  // ONE undoable per burst of moves (panel rule): the X/Y/Z fields move the
  // molecules live on every onChange — each keystroke ("1", "12", "12.5") and
  // each arrow step — and each used to push its own undo entry. The entry is
  // pushed at the FIRST move and later moves are folded into it while it is still
  // the newest entry on the stack. It used to be pushed when this component
  // unmounted — but the menu keeps an opened submenu mounted, so that was when
  // the whole context menu closed, AFTER any Spark or restraint edit made in the
  // same menu. Those record absolute positions / anchors that already include
  // the move, and undoing or redoing them out of order left the molecule 3 Å off
  // or its anchors 6 Å away. Anything recorded in between (or a different
  // selection, or an undo) starts a new entry instead.
  const openRef = useRef<{ indices: number[]; total: Vector3; undoable: UndoableMoleculeTranslation } | null>(null);

  const liveTranslateSelected = (displacement: Vector3) => {
    if (targetIndices.length === 0 || blockedWhileSimulating()) return; // incl. a pause still settling
    translateIndices(targetIndices, displacement);
    const open = openRef.current;
    const sameTargets =
      !!open && open.indices.length === targetIndices.length && open.indices.every((v, k) => v === targetIndices[k]);
    if (open && sameTargets && useStore.getState().undoManager.isLastUndo(open.undoable)) {
      open.total.add(displacement); // undo / redo read the running total
    } else {
      const indices = [...targetIndices];
      const total = displacement.clone();
      const name = indices.length > 1 ? 'Translate Selected Molecules' : 'Translate Selected Molecule';
      const undoable = {
        name,
        timestamp: Date.now(),
        displacement: total,
        blocked: blockedWhileSimulating, // positions are the worker's mid-run
        undo: () => {
          translateIndices(indices, total.clone().negate());
        },
        redo: () => {
          translateIndices(indices, total);
        },
      } as UndoableMoleculeTranslation;
      openRef.current = { indices, total, undoable };
      addUndoable(undoable);
      if (loggable) logAction(name);
    }
    setUpdateFlag(!updateFlag);
  };

  const isMulti = targetIndices.length > 1;
  return (
    targetIndices.length > 0 && (
      <Space
        direction={'vertical'}
        style={{ margin: '6px' }}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={stopMenuKeyboardPropagation}
      >
        <InputNumber
          style={{ width: '140px' }}
          addonBefore={isMulti ? `X (${targetIndices.length})` : 'X'}
          addonAfter={'Å'}
          disabled={startSimulation}
          min={-molecularContainer.lx / 2}
          max={molecularContainer.lx / 2}
          value={parseFloat(center.x.toFixed(1))}
          step={0.1}
          precision={1}
          onChange={(value) => {
            if (value === null) return;
            liveTranslateSelected(new Vector3(value - center.x, 0, 0));
          }}
        />
        <InputNumber
          style={{ width: '140px' }}
          addonBefore={isMulti ? `Y (${targetIndices.length})` : 'Y'}
          addonAfter={'Å'}
          disabled={startSimulation}
          min={-molecularContainer.ly / 2}
          max={molecularContainer.ly / 2}
          value={parseFloat(center.y.toFixed(1))}
          step={0.1}
          precision={1}
          onChange={(value) => {
            if (value === null) return;
            liveTranslateSelected(new Vector3(0, value - center.y, 0));
          }}
        />
        <InputNumber
          style={{ width: '140px' }}
          addonBefore={isMulti ? `Z (${targetIndices.length})` : 'Z'}
          addonAfter={'Å'}
          disabled={startSimulation}
          min={-molecularContainer.lz / 2}
          max={molecularContainer.lz / 2}
          value={parseFloat(center.z.toFixed(1))}
          step={0.1}
          precision={1}
          onChange={(value) => {
            if (value === null) return;
            liveTranslateSelected(new Vector3(0, 0, value - center.z));
          }}
        />
      </Space>
    )
  );
};

export const RotateMolecule = () => {
  const setCommonStore = useStore(Selector.set);
  const setChanged = usePrimitiveStore(Selector.setChanged);
  const loggable = useStore(Selector.loggable);
  const logAction = useStore(Selector.logAction);
  const addUndoable = useStore(Selector.addUndoable);
  const updateViewer = usePrimitiveStore(Selector.updateViewer);
  const pickedIndex = usePrimitiveStore(Selector.pickedMoleculeIndex);
  const pickedMoleculeIndices = usePrimitiveStore(Selector.pickedMoleculeIndices);
  const startSimulation = usePrimitiveStore(Selector.startSimulation);
  const moleculesRef = useRefStore.getState().moleculesRef;

  const { t } = useTranslation();
  const lang = useLanguage();

  // Working set — same fallback logic as TranslateMolecule: multi-select
  // if non-empty, otherwise the single-pick anchor.
  const targetIndices = (() => {
    if (pickedMoleculeIndices.size > 0) return Array.from(pickedMoleculeIndices);
    return pickedIndex !== -1 ? [pickedIndex] : [];
  })();

  const undoableRotateSelectedMolecule = (axis: string, degrees: number) => {
    // Refused while running or while a pause settles — checked HERE, before the
    // entry is pushed: rotateSelectedMolecule bails out then too, so a click in the
    // settling window recorded a rotation that never happened, and Ctrl+Z later
    // rotated the molecule back by a step it had never taken.
    if (targetIndices.length === 0 || !moleculesRef?.current || blockedWhileSimulating()) return;
    const name = targetIndices.length > 1 ? 'Rotate Selected Molecules' : 'Rotate Selected Molecule';
    const undoable = {
      name,
      timestamp: Date.now(),
      axis,
      degrees,
      blocked: blockedWhileSimulating,
      undo: () => {
        rotateSelectedMolecule(axis, -degrees);
      },
      redo: () => {
        rotateSelectedMolecule(axis, degrees);
      },
    } as UndoableMoleculeRotation;
    addUndoable(undoable);
    rotateSelectedMolecule(axis, degrees);
  };

  // Rotates every selected molecule by `degrees` around `axis`. When more
  // than one molecule is selected, the pivot is the group centroid so the
  // selection rotates as one rigid body, preserving relative arrangement.
  // Single-selection still pivots around the molecule's own center.
  const rotateSelectedMolecule = (axis: string, degrees: number) => {
    if (targetIndices.length === 0 || !moleculesRef?.current || blockedWhileSimulating()) return;

    let matrix: Matrix4;
    switch (axis) {
      case 'x':
        matrix = new Matrix4().makeRotationX(Util.toRadians(degrees));
        break;
      case 'y':
        matrix = new Matrix4().makeRotationY(Util.toRadians(degrees));
        break;
      default:
        matrix = new Matrix4().makeRotationZ(Util.toRadians(degrees));
        break;
    }

    // Pivot: group centroid for multi, per-molecule center for single.
    let pivot: Vector3;
    if (targetIndices.length > 1) {
      const sum = new Vector3();
      let count = 0;
      for (const idx of targetIndices) {
        const m = moleculesRef.current[idx];
        if (!m) continue;
        m.updateCenter();
        sum.add(m.center);
        count++;
      }
      pivot = count > 0 ? sum.multiplyScalar(1 / count) : new Vector3();
    } else {
      const m = moleculesRef.current[targetIndices[0]];
      if (!m) return;
      m.updateCenter();
      pivot = m.center.clone();
    }

    for (const idx of targetIndices) {
      const m = moleculesRef.current[idx];
      if (!m) continue;
      for (const a of m.atoms) {
        const p = a.position.clone().sub(pivot).applyMatrix4(matrix).add(pivot);
        a.position.copy(p);
        // A placed molecule is the new Reset target (as with the gizmo).
        a.initialPosition?.copy(a.position);
        // Rotate the restraint anchor about the same pivot — see engineSync.
        transformRestraintAnchor(a, (anchor) => anchor.sub(pivot).applyMatrix4(matrix).add(pivot));
      }
    }

    const liveMolecules = moleculesRef.current;
    setCommonStore((state) => {
      for (const idx of targetIndices) {
        const live = liveMolecules?.[idx];
        const mol = state.projectState.testMolecules[idx];
        if (!live || !mol) continue;
        for (const [i, a] of mol.atoms.entries()) {
          a.position.x = live.atoms[i].position.x;
          a.position.y = live.atoms[i].position.y;
          a.position.z = live.atoms[i].position.z;
          copyRestraintAnchorToStore(a, live.atoms[i]);
        }
      }
    });
    updateViewer();
    setChanged(true);
    markEngineDirty({ moved: true });
  };

  return (
    targetIndices.length > 0 && (
      <Space
        direction={'vertical'}
        style={{ margin: '6px' }}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={stopMenuKeyboardPropagation}
      >
        <Space direction={'horizontal'}>
          <span>{t('molecularViewer.AboutXAxis', lang) + ':'}</span>
          <Button
            disabled={startSimulation}
            onClick={() => {
              undoableRotateSelectedMolecule('x', 5);
              if (loggable) logAction('Rotate Selected Molecule Around X');
            }}
          >
            <RotateLeftOutlined />
          </Button>
          <Button
            disabled={startSimulation}
            onClick={() => {
              undoableRotateSelectedMolecule('x', -5);
              if (loggable) logAction('Rotate Selected Molecule Around X');
            }}
          >
            <RotateRightOutlined />
          </Button>
        </Space>
        <Space direction={'horizontal'}>
          <span>{t('molecularViewer.AboutYAxis', lang) + ':'}</span>
          <Button
            disabled={startSimulation}
            onClick={() => {
              undoableRotateSelectedMolecule('y', 5);
              if (loggable) logAction('Rotate Selected Molecule Around Y');
            }}
          >
            <RotateLeftOutlined />
          </Button>
          <Button
            disabled={startSimulation}
            onClick={() => {
              undoableRotateSelectedMolecule('y', -5);
              if (loggable) logAction('Rotate Selected Molecule Around Y');
            }}
          >
            <RotateRightOutlined />
          </Button>
        </Space>
        <Space direction={'horizontal'}>
          <span>{t('molecularViewer.AboutZAxis', lang) + ':'}</span>
          <Button
            disabled={startSimulation}
            onClick={() => {
              undoableRotateSelectedMolecule('z', 5);
              if (loggable) logAction('Rotate Selected Molecule Around Z');
            }}
          >
            <RotateLeftOutlined />
          </Button>
          <Button
            disabled={startSimulation}
            onClick={() => {
              undoableRotateSelectedMolecule('z', -5);
              if (loggable) logAction('Rotate Selected Molecule Around Z');
            }}
          >
            <RotateRightOutlined />
          </Button>
        </Space>
      </Space>
    )
  );
};

export const CutMolecule = () => {
  const pickedMoleculeIndex = usePrimitiveStore(Selector.pickedMoleculeIndex);

  const { t } = useTranslation();
  const lang = useLanguage();

  // Undoable; shared with the keyboard Ctrl/Cmd+X.
  return (
    <MainMenuItem onClick={() => pickedMoleculeIndex !== -1 && cutMoleculeWithUndo(pickedMoleculeIndex)}>
      {t('word.Cut', lang)}
    </MainMenuItem>
  );
};

// Spark a selected molecule in reactive mode: dissociate it into radicals.
// This is the ONLY way to make radicals in-app — AI generation and the edit
// pipeline both re-saturate valence, and there is no per-atom move. It acts
// directly on the live engine atoms: (1) nudge each atom outward from the
// molecule's centroid so the emergent bond order to its neighbors drops to ~0
// (a true bond "break" in reactive mode means separating atoms, since topology
// is distance-derived), and (2) add an outward kinetic-energy kick (the spark
// energy). For an H2 this yields two H• that ignite surrounding O2 — a spark
// in a cool mixture. Reactive-only: in classical mode bonded topology is fixed,
// so separating atoms would stretch a harmonic spring, not break the bond.
export const SparkMolecule = () => {
  const reactiveMode = useStore(Selector.reactiveMode);
  const pickedMoleculeIndex = usePrimitiveStore(Selector.pickedMoleculeIndex);
  // Mid-run the kick would be overwritten by the next worker packet before it
  // could reach the integrator; paused, the resume sync carries positions AND
  // velocities to the worker. So: spark only while paused.
  const startSimulation = usePrimitiveStore(Selector.startSimulation);
  const setChanged = usePrimitiveStore(Selector.setChanged);
  const loggable = useStore(Selector.loggable);
  const logAction = useStore(Selector.logAction);
  const [energy, setEnergy] = useState<number>(2); // eV deposited per atom

  const { t } = useTranslation();
  const lang = useLanguage();

  if (!reactiveMode) return null;

  const spark = () => {
    if (pickedMoleculeIndex === -1) return;
    const md = useRefStore.getState().molecularDynamicsRef?.current;
    const mol = useRefStore.getState().moleculesRef?.current?.[pickedMoleculeIndex];
    if (!md || !mol || mol.atoms.length === 0) return;
    const atoms = mol.atoms; // same Atom objects the engine integrates
    // Positions, velocities AND charges of the sparked atoms, before and after,
    // so the kick is ONE undoable like Translate / Rotate beside it (it used to
    // register none, so Ctrl-Z after a mis-aimed spark undid the previous action
    // while the radicals flew apart anyway).
    type Frame = { p: Vector3; v: Vector3; q: number }[];
    const capture = (): Frame => atoms.map((a) => ({ p: a.position.clone(), v: a.velocity.clone(), q: a.charge }));
    // Re-solve the (frozen) QEq charges and refresh the bond-order snapshot for
    // the current geometry — otherwise split fragments keep the intact molecule's
    // charges and Coulomb pulls them back together (and vice versa on undo). The
    // resume sync carries positions, velocities and atom.charge to the worker.
    const settle = () => {
      for (const a of md.atoms) a.force?.set(0, 0, 0);
      md.reactiveForceField.requestQeqResolve();
      md.reactiveForceField.compute();
      usePrimitiveStore.getState().set((state) => {
        state.pickedMoleculeIndex = -1;
        state.updateViewerFlag = !state.updateViewerFlag;
      });
      setChanged(true);
    };
    const restore = (frame: Frame) => {
      const live = useRefStore.getState().molecularDynamicsRef?.current;
      if (!live || live !== md) return; // the engine was rebuilt; these atoms are gone
      atoms.forEach((a, i) => {
        a.position.copy(frame[i].p);
        a.velocity.copy(frame[i].v);
        a.charge = frame[i].q;
      });
      settle();
    };
    const before = capture();
    const c = new Vector3();
    for (const a of atoms) c.add(a.position);
    c.multiplyScalar(1 / atoms.length);
    const dir = new Vector3();
    for (const a of atoms) {
      dir.subVectors(a.position, c);
      if (dir.lengthSq() < 1e-8) dir.set(Math.random() - 0.5, Math.random() - 0.5, Math.random() - 0.5);
      dir.normalize();
      // (1) separate so bond order to neighbors → ~0 (real "break" in reactive mode)
      a.position.addScaledVector(dir, 0.5);
      // (2) outward velocity kick: KE = energy ⇒ v = sqrt(2E / (m · EV_CONVERTER))
      const v = Math.sqrt((2 * Math.max(0, energy)) / (a.mass * EV_CONVERTER));
      a.velocity.addScaledVector(dir, v);
    }
    settle();
    const after = capture(); // charges included: settle() just re-solved them
    useStore.getState().addUndoable({
      name: 'Spark Molecule',
      timestamp: Date.now(),
      blocked: blockedWhileSimulating, // positions are the worker's mid-run
      undo: () => restore(before),
      redo: () => restore(after),
    });
    if (loggable) logAction('Spark Molecule (' + energy + ' eV/atom)');
  };

  return (
    <MainMenuItem stayAfterClick>
      {/* width matches the Restraint row above so the two rows align */}
      <Space onClick={(e) => e.stopPropagation()} style={{ width: '240px', justifyContent: 'space-between' }}>
        <Space size={6}>
          <Button disabled={startSimulation} onClick={spark}>
            {t('molecularViewer.Spark', lang)}
          </Button>
          {/* Help icon with a hover Popover — matches the help-icon convention
              used elsewhere (e.g. gallery panels). */}
          <Popover
            trigger="hover"
            placement="bottom"
            title={t('molecularViewer.Spark', lang)}
            content={<div style={{ maxWidth: '260px' }}>{t('molecularViewer.SparkTip', lang)}</div>}
          >
            <QuestionCircleOutlined style={{ color: '#1677ff', fontSize: '13px', cursor: 'help' }} />
          </Popover>
        </Space>
        <InputNumber
          addonAfter={'eV'}
          min={0}
          max={30}
          step={0.5}
          precision={1}
          value={energy}
          onChange={(val) => {
            if (val !== null) setEnergy(val);
          }}
          onKeyDown={stopMenuKeyboardPropagation}
          style={{ width: '120px' }}
        />
      </Space>
    </MainMenuItem>
  );
};

export const CopyMolecule = () => {
  const loggable = useStore(Selector.loggable);
  const logAction = useStore(Selector.logAction);

  const { t } = useTranslation();
  const lang = useLanguage();

  const copySelectedMolecule = () => {
    const pickedMoleculeIndex = usePrimitiveStore.getState().pickedMoleculeIndex;
    if (pickedMoleculeIndex === -1) return;
    const m = useStore.getState().projectState.testMolecules[pickedMoleculeIndex];
    if (!m) return;
    usePrimitiveStore.getState().set((state) => {
      // Live shape + velocities (what the user sees), as Cut does; new-molecule clone.
      state.copiedMolecule = cloneLabMoleculeLive(pickedMoleculeIndex, false) ?? Molecule.clone(m);
      state.cutMolecule = null;
    });
    if (loggable) logAction('Copy Selected Molecule');
  };

  return <MainMenuItem onClick={copySelectedMolecule}>{t('word.Copy', lang)}</MainMenuItem>;
};

export const PasteMolecule = () => {
  const copiedMolecule = usePrimitiveStore(Selector.copiedMolecule);
  const cutMolecule = usePrimitiveStore(Selector.cutMolecule);

  const { t } = useTranslation();
  const lang = useLanguage();

  const undoablePasteSelectedMolecule = () => {
    pasteMoleculeWithUndo();
  };

  return (
    <MainMenuItem hasPadding={true} onClick={undoablePasteSelectedMolecule}>
      {t('word.Paste', lang) + (cutMolecule ? ' ' + cutMolecule.name : copiedMolecule ? ' ' + copiedMolecule.name : '')}
    </MainMenuItem>
  );
};

/**
 * Fix / unfix EVERY atom of the picked molecule (or of the molecule
 * multi-selection), the context-menu twin of the agent's fixLabMolecule. The
 * per-atom checkbox needs the atoms picked one by one, which a gold plate of a
 * thousand atoms dropped from the gallery made impractical — and a plate must
 * be fixed as a whole before Play, or it sags under whatever stands on it. One
 * undo step for the whole molecule; the engine's flags are kept in step the way
 * the per-atom checkbox does it.
 */
export const FixMoleculeCheckBox = () => {
  const updateViewer = usePrimitiveStore(Selector.updateViewer);
  const getAtomByIndex = useStore(Selector.getAtomByIndex);
  const fixAtomByIndex = useStore(Selector.fixAtomByIndex);
  const testMolecules = useStore(Selector.testMolecules);
  const pickedMoleculeIndex = usePrimitiveStore(Selector.pickedMoleculeIndex);
  const pickedMoleculeIndices = usePrimitiveStore(Selector.pickedMoleculeIndices);
  const setChanged = usePrimitiveStore(Selector.setChanged);
  const loggable = useStore(Selector.loggable);
  const logAction = useStore(Selector.logAction);
  const updateViewerFlag = usePrimitiveStore(Selector.updateViewerFlag);
  const mdRef = useRefStore.getState().molecularDynamicsRef;

  const { t } = useTranslation();
  const lang = useLanguage();

  // The lab numbers atoms across its molecules in order, so a molecule's atoms
  // are a contiguous run starting at the sum of the earlier molecules' sizes.
  // Target: the multi-selection if there is one, else the picked molecule.
  const targetIndices = useMemo(() => {
    const mols =
      pickedMoleculeIndices.size > 0
        ? Array.from(pickedMoleculeIndices)
        : pickedMoleculeIndex !== -1
          ? [pickedMoleculeIndex]
          : [];
    const starts: number[] = [];
    let acc = 0;
    for (const m of testMolecules) {
      starts.push(acc);
      acc += m.atoms.length;
    }
    const out: number[] = [];
    for (const mi of mols) {
      const m = testMolecules[mi];
      if (!m) continue;
      for (let k = 0; k < m.atoms.length; k++) out.push(starts[mi] + k);
    }
    return out;
  }, [pickedMoleculeIndices, pickedMoleculeIndex, testMolecules]);

  // Same tri-state and the same re-read-on-updateViewer rule as FixAtomCheckBox
  // (the atoms are mutated in place, so the store reference never changes).
  const fixedStates = useMemo(
    () => targetIndices.map((i) => !!getAtomByIndex(i)?.fixed),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [targetIndices, updateViewerFlag],
  );
  const allFixed = fixedStates.length > 0 && fixedStates.every(Boolean);
  const noneFixed = fixedStates.every((f) => !f);
  const indeterminate = !allFixed && !noneFixed;

  const applyFixed = (next: (k: number) => boolean) => {
    targetIndices.forEach((i, k) => {
      const v = next(k);
      fixAtomByIndex(i, v);
      mdRef?.current?.setAtomFixed(i, v);
    });
    setChanged(true);
    updateViewer();
    markEngineDirty();
  };

  return (
    <MainMenuItem stayAfterClick>
      <Checkbox
        style={{ width: '100%' }}
        checked={allFixed}
        indeterminate={indeterminate}
        disabled={targetIndices.length === 0}
        onChange={(e: CheckboxChangeEvent) => {
          const next = e.target.checked;
          const prev = fixedStates.slice();
          runWithUndo(
            next ? 'Fix Molecule' : 'Unfix Molecule',
            () => applyFixed((k) => prev[k]),
            () => applyFixed(() => next),
          );
          if (loggable) logAction(next ? 'Fix molecule' : 'Unfix molecule');
        }}
      >
        {t('experiment.FixMolecule', lang)}
        {targetIndices.length > 0 ? ` (${targetIndices.length})` : ''}
      </Checkbox>
    </MainMenuItem>
  );
};

export const RestrainMoleculeInputField = () => {
  const setCommonStore = useStore(Selector.set);
  const setChanged = usePrimitiveStore(Selector.setChanged);
  const testMolecules = useStore(Selector.testMolecules);
  const pickedIndex = usePrimitiveStore(Selector.pickedMoleculeIndex);
  const moleculesRef = useRefStore.getState().moleculesRef;

  const { t } = useTranslation();
  const lang = useLanguage();

  const [value, setValue] = useState<number>(0);

  const restraint = useMemo(() => {
    if (pickedIndex !== -1 && testMolecules[pickedIndex]) return testMolecules[pickedIndex].atoms[0].restraint;
    return null;
  }, [pickedIndex, testMolecules]);

  useEffect(() => {
    setValue(restraint?.strength ?? 0);
  }, [restraint]);

  const setRestraint = (value: number) => {
    // A stale index (undo/redo after the molecule list changed, or an edit while
    // the live list is still being rebuilt) reads past the end and gets
    // undefined, which the old `=== null` test let through to crash on
    // mol.atoms. Also require the store molecule to line up atom for atom —
    // its new restraints are anchored at the live atoms' positions.
    const mol = moleculesRef?.current?.[pickedIndex];
    const storeMol = useStore.getState().projectState.testMolecules[pickedIndex];
    if (!mol || !storeMol || storeMol.atoms.length !== mol.atoms.length) return;
    // Decide create / update / clear PER ATOM from the CURRENT restraint state,
    // not from the `restraint` value captured when the field rendered. undo/redo
    // call this later (after the state has changed), so branching on a stale
    // snapshot broke them — e.g. redoing a freshly-created restraint hit the
    // "update existing" branch and no-op'd because undo had already cleared it.
    // Store and live atoms get SEPARATE Restraint objects so neither mutates an
    // Immer-frozen instance shared with the other.
    setCommonStore((state) => {
      const testMolecule = state.projectState.testMolecules[pickedIndex];
      if (!testMolecule) return;
      for (let i = 0; i < testMolecule.atoms.length; i++) {
        const storeAtom = testMolecule.atoms[i];
        if (value > 0) {
          if (storeAtom.restraint) (storeAtom.restraint as Restraint).strength = value;
          else storeAtom.restraint = new Restraint(value, mol.atoms[i].position.clone());
        } else {
          storeAtom.restraint = undefined;
        }
      }
    });
    // Live render/engine atoms (shared by reference with molecularDynamicsRef).
    for (const a of mol.atoms) {
      if (value > 0) {
        if (a.restraint) a.restraint.strength = value;
        else a.restraint = new Restraint(value, a.position.clone());
      } else {
        a.restraint = undefined;
      }
    }
    setChanged(true);
    markEngineDirty();
  };

  // Undo / redo put back each atom's restraint STRENGTH from before / after
  // this edit (atoms can carry different strengths, e.g. set by the agent), and
  // keep or re-anchor exactly as typing the value would — see
  // applyRestraintSnapshot for why saved anchors are not restored. Snapshot on
  // focus, register on blur or unmount: one entry per visit, like the other
  // numeric fields.
  const restraintStartRef = useRef<RestraintSnapshot | null>(null);
  const commitRestraint = () => {
    const before = restraintStartRef.current;
    restraintStartRef.current = null;
    if (!before) return;
    const after = snapshotRestraints(before.index);
    if (!after || after.name !== before.name || after.strengths.length !== before.strengths.length) return;
    if (before.strengths.every((v, i) => v === after.strengths[i])) return;
    runWithUndo(
      'Set Molecule Restraint',
      () => applyRestraintSnapshot(before),
      () => applyRestraintSnapshot(after),
    );
  };
  const commitRestraintRef = useRef(commitRestraint);
  commitRestraintRef.current = commitRestraint;
  useEffect(() => () => commitRestraintRef.current(), []);

  return (
    <MainMenuItem stayAfterClick={true}>
      {/* width matches the Spark row so the two rows align; label + help icon
          on the left (like the Spark button + icon), input on the right */}
      <Space onClick={(e) => e.stopPropagation()} style={{ width: '240px', justifyContent: 'space-between' }}>
        <Space size={6}>
          <span>{t('experiment.Restraint', lang) + ':'}</span>
          {/* Help icon with a hover Popover — same convention as the Spark row. */}
          <Popover
            trigger="hover"
            placement="bottom"
            title={t('experiment.Restraint', lang)}
            content={<div style={{ maxWidth: '260px' }}>{t('experiment.RestraintTip', lang)}</div>}
          >
            <QuestionCircleOutlined style={{ color: '#1677ff', fontSize: '13px', cursor: 'help' }} />
          </Popover>
        </Space>
        <InputNumber
          onKeyDown={stopMenuKeyboardPropagation}
          addonAfter={'eV/Å²'}
          min={0}
          max={100}
          precision={2}
          // make sure that we round up the number as toDegrees may cause things like .999999999
          value={parseFloat(value.toFixed(2))}
          step={0.01}
          style={{ width: '130px' }}
          onFocus={() => {
            restraintStartRef.current = pickedIndex !== -1 ? snapshotRestraints(pickedIndex) : null;
          }}
          onBlur={commitRestraint}
          onChange={(s) => {
            if (s === null) return;
            setValue(s);
            setRestraint(s);
          }}
        />
      </Space>
    </MainMenuItem>
  );
};

export const TrajectoryCheckBox = () => {
  const pickedAtomIndex = usePrimitiveStore(Selector.pickedAtomIndex);
  const getAtomByIndex = useStore(Selector.getAtomByIndex);
  const updateViewer = usePrimitiveStore(Selector.updateViewer);
  const setAtomTrajectoryByIndex = useStore(Selector.setAtomTrajectoryByIndex);
  const positionTimeSeriesMap = useDataStore(Selector.positionTimeSeriesMap);
  const setChanged = usePrimitiveStore(Selector.setChanged);
  const loggable = useStore(Selector.loggable);
  const logAction = useStore(Selector.logAction);
  const addUndoable = useStore(Selector.addUndoable);
  const mdRef = useRefStore.getState().molecularDynamicsRef;

  const { t } = useTranslation();
  const lang = useLanguage();

  const setTrajectory = (checked: boolean) => {
    if (pickedAtomIndex === -1) return;
    setAtomTrajectoryByIndex(pickedAtomIndex, checked);
    if (mdRef?.current) {
      mdRef.current.atoms[pickedAtomIndex].trajectory = checked;
      setChecked(checked);
    }
    if (positionTimeSeriesMap) {
      positionTimeSeriesMap.delete(pickedAtomIndex);
      updateViewer();
    }
    setChanged(true);
  };

  const [checked, setChecked] = useState(!!getAtomByIndex(pickedAtomIndex)?.trajectory);
  useEffect(() => {
    setChecked(!!getAtomByIndex(pickedAtomIndex)?.trajectory);
  }, [pickedAtomIndex]);

  return (
    <MainMenuItem stayAfterClick>
      <Checkbox
        style={{ width: '100%' }}
        checked={checked}
        onChange={(e: CheckboxChangeEvent) => {
          const checked = e.target.checked;
          setWithUndo('Show Atom Trajectory', !checked, checked, setTrajectory);
          if (loggable) logAction(checked ? 'Show atom trajectory' : 'Hide atom trajectory');
        }}
      >
        {t('molecularViewer.Trajectory', lang)}
      </Checkbox>
    </MainMenuItem>
  );
};

export const FixAtomCheckBox = () => {
  const updateViewer = usePrimitiveStore(Selector.updateViewer);
  const getAtomByIndex = useStore(Selector.getAtomByIndex);
  const fixAtomByIndex = useStore(Selector.fixAtomByIndex);
  const pickedAtomIndex = usePrimitiveStore(Selector.pickedAtomIndex);
  const pickedAtomIndices = usePrimitiveStore(Selector.pickedAtomIndices);
  const setChanged = usePrimitiveStore(Selector.setChanged);
  const loggable = useStore(Selector.loggable);
  const logAction = useStore(Selector.logAction);
  const updateViewerFlag = usePrimitiveStore(Selector.updateViewerFlag);
  const mdRef = useRefStore.getState().molecularDynamicsRef;

  const { t } = useTranslation();
  const lang = useLanguage();

  // Resolve the working set: the multi-selection if non-empty, otherwise
  // fall back to the single-pick anchor. Keeps the single-atom UX
  // identical when nothing was shift-clicked.
  const targetIndices = useMemo(() => {
    if (pickedAtomIndices.size > 0) return Array.from(pickedAtomIndices);
    return pickedAtomIndex !== -1 ? [pickedAtomIndex] : [];
  }, [pickedAtomIndices, pickedAtomIndex]);

  // Tri-state for the checkbox: all fixed → checked, none fixed → unchecked,
  // mixed → indeterminate. A memo keyed on the selection alone never re-ran
  // after a click — the box stayed empty, every click sent "fix = true" again,
  // and an atom could not be unfixed from here. Subscribing to testMolecules
  // does not help either: the store holds Molecule / Atom CLASS instances, which
  // Immer leaves undrafted (nothing is `immerable`), so fixAtomByIndex mutates
  // the atom in place and the testMolecules reference never changes. The
  // signal every fix path already sends — this menu, its undo, the agent's
  // fixAtoms — is updateViewer(); key on that flag and re-read the (mutated)
  // atoms through getAtomByIndex.
  const fixedStates = useMemo(
    () => targetIndices.map((i) => !!getAtomByIndex(i)?.fixed),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [targetIndices, updateViewerFlag],
  );
  const allFixed = fixedStates.length > 0 && fixedStates.every(Boolean);
  const noneFixed = fixedStates.every((f) => !f);
  const indeterminate = !allFixed && !noneFixed;

  const applyFixed = (next: boolean, indices: number[]) => {
    for (const i of indices) {
      fixAtomByIndex(i, next);
      mdRef?.current?.setAtomFixed(i, next);
    }
    setChanged(true);
    updateViewer();
    markEngineDirty();
  };

  return (
    <MainMenuItem stayAfterClick>
      <Checkbox
        style={{ width: '100%' }}
        checked={allFixed}
        indeterminate={indeterminate}
        disabled={targetIndices.length === 0}
        onChange={(e: CheckboxChangeEvent) => {
          // Single op for the whole selection so Ctrl-Z restores every
          // atom's previous fixed state in one step. The "next" value is
          // applied uniformly across the selection.
          const next = e.target.checked;
          const indices = [...targetIndices];
          const prev = indices.map((i) => !!getAtomByIndex(i)?.fixed);
          runWithUndo(
            indices.length > 1 ? 'Fix Selected Atoms' : 'Fix Selected Atom',
            () => {
              // Undo: restore each atom's prior state.
              for (let k = 0; k < indices.length; k++) {
                fixAtomByIndex(indices[k], prev[k]);
                mdRef?.current?.setAtomFixed(indices[k], prev[k]);
              }
              setChanged(true);
              updateViewer();
              markEngineDirty();
            },
            () => applyFixed(next, indices),
          );
          if (loggable) logAction(next ? 'Fix selected atoms' : 'Unfix selected atoms');
        }}
      >
        {t('experiment.FixAtom', lang)}
        {targetIndices.length > 1 ? ` (${targetIndices.length})` : ''}
      </Checkbox>
    </MainMenuItem>
  );
};

export const RestrainAtomInputField = () => {
  const getAtomByIndex = useStore(Selector.getAtomByIndex);
  const restrainAtomByIndex = useStore(Selector.restrainAtomByIndex);
  const pickedIndex = usePrimitiveStore(Selector.pickedAtomIndex);
  const setChanged = usePrimitiveStore(Selector.setChanged);
  const updateViewer = usePrimitiveStore(Selector.updateViewer);
  const mdRef = useRefStore.getState().molecularDynamicsRef;

  const { t } = useTranslation();
  const lang = useLanguage();

  const [value, setValue] = useState<number>(0);

  const restraint = useMemo(() => {
    if (pickedIndex !== -1) {
      const a = getAtomByIndex(pickedIndex);
      if (a) return a.restraint;
    }
    return null;
  }, [pickedIndex]);

  useEffect(() => {
    setValue(restraint?.strength ?? 0);
  }, [restraint]);

  const setStrength = (strength: number) => {
    if (pickedIndex !== -1) {
      // Anchor the STORE restraint at the live atom's position too: the store
      // atom sits at the last saved frame, and a restraint pinned there pulled
      // the atom back to it on the next engine rebuild (see restrainAtomByIndex).
      restrainAtomByIndex(pickedIndex, strength, mdRef?.current?.atoms[pickedIndex]?.position);
      setChanged(true);
      if (mdRef?.current) {
        const a = mdRef.current.atoms[pickedIndex];
        if (a) {
          if (a.restraint) {
            if (strength > 0) {
              a.restraint.strength = strength;
            } else {
              a.restraint = undefined;
            }
          } else {
            if (strength > 0) {
              a.restraint = new Restraint(strength, a.position.clone());
            }
          }
        }
        markEngineDirty();
      }
      // No updateViewer() — a restraint-strength change doesn't alter geometry;
      // forcing a full rebuild here caused the scene to look like it reloaded.
    }
  };

  const strengthUndo = useNumericFieldUndo('Set Atom Restraint', setStrength, value);

  return (
    <MainMenuItem stayAfterClick>
      <InputNumber
        onKeyDown={stopMenuKeyboardPropagation}
        addonBefore={t('experiment.Restraint', lang) + ':'}
        addonAfter={'eV/Å²'}
        min={0}
        max={100}
        precision={2}
        style={{ width: '250px' }}
        // make sure that we round up the number as toDegrees may cause things like .999999999
        value={parseFloat(value.toFixed(2))}
        step={0.01}
        onFocus={() => strengthUndo.begin(value)}
        onBlur={() => strengthUndo.commit(value)}
        onChange={(s) => {
          if (s === null) return;
          setValue(s);
          setStrength(s);
        }}
      />
    </MainMenuItem>
  );
};

export const DampAtomInputField = () => {
  const getAtomByIndex = useStore(Selector.getAtomByIndex);
  const dampAtomByIndex = useStore(Selector.dampAtomByIndex);
  const pickedIndex = usePrimitiveStore(Selector.pickedAtomIndex);
  const setChanged = usePrimitiveStore(Selector.setChanged);
  const updateViewer = usePrimitiveStore(Selector.updateViewer);
  const mdRef = useRefStore.getState().molecularDynamicsRef;

  const { t } = useTranslation();
  const lang = useLanguage();

  const [value, setValue] = useState<number>(0);

  const damp = useMemo(() => {
    if (pickedIndex !== -1) {
      const a = getAtomByIndex(pickedIndex);
      if (a) return a.damp ?? 0;
    }
    return 0;
  }, [pickedIndex]);

  useEffect(() => {
    setValue(damp);
  }, [damp]);

  const setDamp = (damp: number) => {
    if (pickedIndex !== -1) {
      dampAtomByIndex(pickedIndex, damp);
      setChanged(true);
      if (mdRef?.current) {
        const a = mdRef.current.atoms[pickedIndex];
        if (a) a.damp = damp;
        markEngineDirty();
      }
      // No updateViewer() — damping has no visual; a full rebuild here made the
      // scene look like it reloaded on a property edit.
    }
  };

  const dampUndo = useNumericFieldUndo('Set Damping Coefficient', setDamp, value);

  return (
    <MainMenuItem stayAfterClick>
      <InputNumber
        onKeyDown={stopMenuKeyboardPropagation}
        addonBefore={t('experiment.DampingCoefficient', lang) + ':'}
        addonAfter={'eV⋅fs/Å²'}
        min={0}
        max={5}
        precision={2}
        style={{ width: '250px' }}
        // make sure that we round up the number as toDegrees may cause things like .999999999
        value={parseFloat(value.toFixed(2))}
        step={0.01}
        onFocus={() => dampUndo.begin(value)}
        onBlur={() => dampUndo.commit(value)}
        onChange={(s) => {
          if (s === null) return;
          setValue(s);
          setDamp(s);
        }}
      />
    </MainMenuItem>
  );
};

export const ChargeAtomInputField = () => {
  const getAtomByIndex = useStore(Selector.getAtomByIndex);
  const chargeAtomByIndex = useStore(Selector.chargeAtomByIndex);
  const pickedIndex = usePrimitiveStore(Selector.pickedAtomIndex);
  const setChanged = usePrimitiveStore(Selector.setChanged);
  const updateViewer = usePrimitiveStore(Selector.updateViewer);
  // Subscribe to testMolecules so the displayed charge refreshes when a
  // gallery edit swaps a chamber instance (or a fresh molecule is dropped
  // in). Without this, the useMemo below would only re-run on pickedIndex
  // changes — reopening the menu after a swap would show the stale value.
  const testMolecules = useStore(Selector.testMolecules);
  const mdRef = useRefStore.getState().molecularDynamicsRef;

  const { t } = useTranslation();
  const lang = useLanguage();

  const [value, setValue] = useState<number>(0);

  const charge = useMemo(() => {
    if (pickedIndex !== -1) {
      // The engine's charge when there is one: the species table (TIP3P water,
      // the N₂ / O₂ quadrupole models) sets charges the stored atom lacks.
      const live = mdRef?.current?.atoms[pickedIndex];
      if (live) return live.charge ?? 0;
      const a = getAtomByIndex(pickedIndex);
      if (a) return a.charge ?? 0;
    }
    return 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickedIndex, testMolecules]);
  // The massless bond-center charge of this atom's molecule (quadrupole model).
  const siteCharge = useMemo(() => {
    const live = pickedIndex !== -1 ? mdRef?.current?.atoms[pickedIndex] : undefined;
    return live ? mdRef?.current?.virtualSiteOf(live)?.charge : undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickedIndex, testMolecules, value]);

  useEffect(() => {
    setValue(charge);
  }, [charge]);

  const applyCharge = (index: number, charge: number, userSet = true) => {
    if (index !== -1) {
      chargeAtomByIndex(index, charge, userSet);
      setChanged(true);
      if (mdRef?.current) {
        const a = mdRef.current.atoms[index];
        if (a) {
          a.charge = charge;
          if (userSet) a.chargeSet = true;
          else delete a.chargeSet;
        }
        // A hand-set charge takes an N₂ / O₂ off its quadrupole model (undo puts
        // it back), so re-decide the bond-center sites before the sync ships them.
        mdRef.current.refreshQuadrupoleSites();
        // Charged pairs are listed out to the Coulomb radius, neutral ones only
        // to the LJ radius, so the Verlet list must be rebuilt for the new charge.
        // On the no-worker fallback the mirror IS the stepped engine and nothing
        // else forces that mid-run (the worker compares shipped charges itself).
        mdRef.current.nonBondedInteractions.invalidateNeighborList();
        markEngineDirty();
      }
      // No updateViewer() — a charge change doesn't alter geometry, and forcing
      // a full visual rebuild here is what made editing a property read as the
      // whole scene reloading (see dynamicsViewer's rebuild effect).
    }
  };

  const setCharge = (charge: number) => applyCharge(pickedIndex, charge);

  // One entry per visit, like useNumericFieldUndo, but the start also records
  // whether the charge was user-set (Atom.chargeSet): undo puts that flag back
  // too, so a computed charge returns to computed. Replaying the number alone
  // flagged it user-set, which froze it against recomputation (load, a gallery
  // edit refreshing atomCharges) and saved it as a hand-set charge.
  const chargeStartRef = useRef<{ index: number; charge: number; userSet: boolean } | null>(null);
  const latestChargeRef = useRef(value);
  latestChargeRef.current = value;
  const commitCharge = (current: number) => {
    const start = chargeStartRef.current;
    chargeStartRef.current = null;
    if (!start || start.charge === current) return;
    runWithUndo(
      'Set Electric Charge',
      () => applyCharge(start.index, start.charge, start.userSet),
      () => applyCharge(start.index, current),
    );
  };
  const commitChargeRef = useRef(commitCharge);
  commitChargeRef.current = commitCharge;
  useEffect(() => () => commitChargeRef.current(latestChargeRef.current), []);
  const chargeUndo = {
    begin: (current: number) => {
      chargeStartRef.current =
        pickedIndex !== -1
          ? { index: pickedIndex, charge: current, userSet: !!getAtomByIndex(pickedIndex)?.chargeSet }
          : null;
    },
    commit: commitCharge,
  };

  return (
    <MainMenuItem stayAfterClick>
      <InputNumber
        onKeyDown={stopMenuKeyboardPropagation}
        addonBefore={t('experiment.ElectricCharge', lang) + ':'}
        addonAfter={'e'}
        min={-5}
        max={5}
        precision={2}
        style={{ width: '250px' }}
        // make sure that we round up the number as toDegrees may cause things like .999999999
        value={parseFloat(value.toFixed(2))}
        step={0.01}
        onFocus={() => chargeUndo.begin(value)}
        onBlur={() => chargeUndo.commit(value)}
        onChange={(s) => {
          if (s === null) return;
          setValue(s);
          setCharge(s);
        }}
      />
      {siteCharge !== undefined && (
        <div style={{ fontSize: '12px', color: 'gray', paddingTop: '4px' }}>
          {t('experiment.BondCenterCharge', lang) + ': ' + (siteCharge > 0 ? '+' : '') + siteCharge.toFixed(3) + ' e'}
        </div>
      )}
    </MainMenuItem>
  );
};

export const VdwBondsCheckBox = () => {
  const visible = useStore(Selector.vdwBondsVisible);
  const setChanged = usePrimitiveStore(Selector.setChanged);
  const loggable = useStore(Selector.loggable);
  const logAction = useStore(Selector.logAction);
  const addUndoable = useStore(Selector.addUndoable);

  const { t } = useTranslation();
  const lang = useLanguage();

  const setVisible = (checked: boolean) => {
    useStore.getState().set((state) => {
      state.projectState.vdwBondsVisible = checked;
    });
    setChanged(true);
  };

  return (
    <MainMenuItem stayAfterClick={true}>
      <Checkbox
        style={{ width: '100%' }}
        checked={visible}
        onChange={(e: CheckboxChangeEvent) => {
          const checked = e.target.checked;
          setWithUndo('Show Van der Waals Bonds', !checked, checked, setVisible);
          if (loggable) logAction(checked ? 'Show Van der Waals bonds' : 'Hide Van der Waals bonds');
        }}
      >
        {t('molecularViewer.VanDerWaalsBonds', lang)}
      </Checkbox>
    </MainMenuItem>
  );
};

export const HBondsCheckBox = () => {
  const visible = useStore(Selector.hBondsVisible);
  const setChanged = usePrimitiveStore(Selector.setChanged);
  const loggable = useStore(Selector.loggable);
  const logAction = useStore(Selector.logAction);
  const { t } = useTranslation();
  const lang = useLanguage();

  const setVisible = (checked: boolean) => {
    useStore.getState().set((state) => {
      state.projectState.hBondsVisible = checked;
    });
    setChanged(true);
  };

  return (
    <MainMenuItem stayAfterClick={true}>
      <Checkbox
        style={{ width: '100%' }}
        checked={visible}
        onChange={(e: CheckboxChangeEvent) => {
          const checked = e.target.checked;
          setWithUndo('Show Hydrogen Bonds', !checked, checked, setVisible);
          if (loggable) logAction(checked ? 'Show hydrogen bonds' : 'Hide hydrogen bonds');
        }}
      >
        {t('molecularViewer.HydrogenBonds', lang)}
      </Checkbox>
    </MainMenuItem>
  );
};

export const KineticEnergyColorsCheckBox = () => {
  const visible = useStore(Selector.kineticEnergyColorVisible);
  const setChanged = usePrimitiveStore(Selector.setChanged);
  const loggable = useStore(Selector.loggable);
  const logAction = useStore(Selector.logAction);
  const { t } = useTranslation();
  const lang = useLanguage();

  const setVisible = (checked: boolean) => {
    useStore.getState().set((state) => {
      state.projectState.kineticEnergyColorVisible = checked;
    });
    setChanged(true);
  };

  return (
    <MainMenuItem stayAfterClick={true}>
      <Checkbox
        style={{ width: '100%' }}
        checked={visible}
        onChange={(e: CheckboxChangeEvent) => {
          const checked = e.target.checked;
          setWithUndo('Show Kinetic Energy Colors', !checked, checked, setVisible);
          if (loggable) logAction(checked ? 'Show kinetic energy colors' : 'Hide kinetic energy colors');
        }}
      >
        {t('molecularViewer.KineticEnergyColors', lang)}
      </Checkbox>
    </MainMenuItem>
  );
};

export const DisulfideBondsCheckBox = () => {
  const visible = useStore(Selector.disulfideBondsVisible);
  const setChanged = usePrimitiveStore(Selector.setChanged);
  const loggable = useStore(Selector.loggable);
  const logAction = useStore(Selector.logAction);
  const { t } = useTranslation();
  const lang = useLanguage();

  const setVisible = (checked: boolean) => {
    useStore.getState().set((state) => {
      state.projectState.disulfideBondsVisible = checked;
    });
    setChanged(true);
  };

  return (
    <MainMenuItem stayAfterClick={true}>
      <Checkbox
        style={{ width: '100%' }}
        checked={visible}
        onChange={(e: CheckboxChangeEvent) => {
          const checked = e.target.checked;
          setWithUndo('Show Disulfide Bonds', !checked, checked, setVisible);
          if (loggable) logAction(checked ? 'Show disulfide bonds' : 'Hide disulfide bonds');
        }}
      >
        {t('molecularViewer.DisulfideBonds', lang)}
      </Checkbox>
    </MainMenuItem>
  );
};

export const AngularBondsCheckBox = () => {
  const visible = useStore(Selector.angularBondsVisible);
  const setChanged = usePrimitiveStore(Selector.setChanged);
  const loggable = useStore(Selector.loggable);
  const logAction = useStore(Selector.logAction);
  const addUndoable = useStore(Selector.addUndoable);

  const { t } = useTranslation();
  const lang = useLanguage();

  const setVisible = (checked: boolean) => {
    useStore.getState().set((state) => {
      state.projectState.angularBondsVisible = checked;
    });
    setChanged(true);
  };

  return (
    <MainMenuItem stayAfterClick={true}>
      <Checkbox
        style={{ width: '100%' }}
        checked={visible}
        onChange={(e: CheckboxChangeEvent) => {
          const checked = e.target.checked;
          setWithUndo('Show Angular Bonds', !checked, checked, setVisible);
          if (loggable) logAction(checked ? 'Show angular bonds' : 'Hide angular bonds');
        }}
      >
        {t('molecularViewer.AngularBonds', lang)}
      </Checkbox>
    </MainMenuItem>
  );
};

export const TorsionalBondsCheckBox = () => {
  const visible = useStore(Selector.torsionalBondsVisible);
  const setChanged = usePrimitiveStore(Selector.setChanged);
  const loggable = useStore(Selector.loggable);
  const logAction = useStore(Selector.logAction);
  const addUndoable = useStore(Selector.addUndoable);

  const { t } = useTranslation();
  const lang = useLanguage();

  const setVisible = (checked: boolean) => {
    useStore.getState().set((state) => {
      state.projectState.torsionalBondsVisible = checked;
    });
    setChanged(true);
  };

  return (
    <MainMenuItem stayAfterClick={true}>
      <Checkbox
        style={{ width: '100%' }}
        checked={visible}
        onChange={(e: CheckboxChangeEvent) => {
          const checked = e.target.checked;
          setWithUndo('Show Torsional Bonds', !checked, checked, setVisible);
          if (loggable) logAction(checked ? 'Show torsional bonds' : 'Hide torsional bonds');
        }}
      >
        {t('molecularViewer.TorsionalBonds', lang)}
      </Checkbox>
    </MainMenuItem>
  );
};

export const MomentumVectorCheckBox = () => {
  const visible = useStore(Selector.momentumVisible);
  const setChanged = usePrimitiveStore(Selector.setChanged);
  const loggable = useStore(Selector.loggable);
  const logAction = useStore(Selector.logAction);
  const addUndoable = useStore(Selector.addUndoable);

  const { t } = useTranslation();
  const lang = useLanguage();

  const setVisible = (checked: boolean) => {
    useStore.getState().set((state) => {
      state.projectState.momentumVisible = checked;
    });
    setChanged(true);
  };

  return (
    <MainMenuItem stayAfterClick={true}>
      <Checkbox
        style={{ width: '100%' }}
        checked={visible}
        onChange={(e: CheckboxChangeEvent) => {
          const checked = e.target.checked;
          setWithUndo('Show Momentum Vectors', !checked, checked, setVisible);
          if (loggable) logAction(checked ? 'Show momentum vectors' : 'Hide momentum vectors');
        }}
      >
        {t('molecularViewer.MomentumVectors', lang)}
      </Checkbox>
    </MainMenuItem>
  );
};

export const ForceVectorCheckBox = () => {
  const visible = useStore(Selector.forceVisible);
  const setChanged = usePrimitiveStore(Selector.setChanged);
  const loggable = useStore(Selector.loggable);
  const logAction = useStore(Selector.logAction);
  const addUndoable = useStore(Selector.addUndoable);

  const { t } = useTranslation();
  const lang = useLanguage();

  const setVisible = (checked: boolean) => {
    useStore.getState().set((state) => {
      state.projectState.forceVisible = checked;
    });
    setChanged(true);
  };

  return (
    <MainMenuItem stayAfterClick={true}>
      <Checkbox
        style={{ width: '100%' }}
        checked={visible}
        onChange={(e: CheckboxChangeEvent) => {
          const checked = e.target.checked;
          setWithUndo('Show Force Vectors', !checked, checked, setVisible);
          if (loggable) logAction(checked ? 'Show force vectors' : 'Hide force vectors');
        }}
      >
        {t('molecularViewer.ForceVectors', lang)}
      </Checkbox>
    </MainMenuItem>
  );
};

export const IndividualMoleculeStyleRadioGroup = () => {
  const testMolecules = useStore(Selector.testMolecules);
  const molecularViewerStyle = useStore(Selector.chamberViewerStyle);
  const pickedMoleculeIndex = usePrimitiveStore(Selector.pickedMoleculeIndex);
  const setChanged = usePrimitiveStore(Selector.setChanged);
  const { t } = useTranslation();
  const lang = useLanguage();

  // `undefined` CLEARS the molecule's own style so it follows the lab-wide one
  // again. Undo relies on this: a molecule that was inheriting must go back to
  // inheriting, not be pinned to whatever the lab's style happened to be.
  const setStyle = (style: MolecularViewerStyle | undefined) => {
    useStore.getState().set((state) => {
      const mol = state.projectState.testMolecules[pickedMoleculeIndex];
      if (!mol) return;
      if (style === undefined) delete mol.style;
      else mol.style = style;
    });
    usePrimitiveStore.getState().updateViewer();
    setChanged(true);
  };

  const multipleResidues = useMemo(() => {
    return !!testMolecules[pickedMoleculeIndex]?.multipleResidues;
  }, [testMolecules, pickedMoleculeIndex]);

  // @szhsin/react-menu's MenuRadioGroup inside a ControlledMenu can fail to
  // update the radio dot's `--checked` class on click — the store mutation
  // and external re-render happen, but the dot stays on the previous value
  // (the renderer still picks up the new style fine, only the dot is stale).
  // Driving the radio with local state and updating it synchronously inside
  // the click handler — before the store mutation that triggers external
  // re-render — sidesteps that, while the useEffect keeps the dot in sync
  // with external store changes (undo/redo, picking a different molecule).
  const ownStyle = testMolecules[pickedMoleculeIndex]?.style; // undefined = inherits the lab's
  const storeStyle = ownStyle ?? molecularViewerStyle;
  const [radioValue, setRadioValue] = useState<MolecularViewerStyle>(storeStyle);
  useEffect(() => {
    setRadioValue(storeStyle);
  }, [storeStyle]);

  const onClick = (e: ClickEvent) => {
    e.keepOpen = true;
  };

  return (
    <MainSubMenu label={t('molecularViewer.Style', lang)}>
      <MenuRadioGroup
        value={radioValue}
        onRadioChange={(e: RadioChangeEvent) => {
          const next = e.value as MolecularViewerStyle;
          // The menu fires for a click on the radio already showing the dot. Compare
          // against the DISPLAYED value (the inherited one for a molecule with no
          // style of its own): no change, no undo step — and no silent pin of an
          // inheriting molecule to the lab's current style.
          if (next === storeStyle) return;
          setRadioValue(next);
          // Record the field as it IS (undefined when inheriting), not the value
          // the radio displays — undoing used to write the inherited style onto
          // the molecule and silently pin it.
          setWithUndo('Select Style for Selected Molecule', ownStyle, next, setStyle);
        }}
      >
        {INDIVIDUAL_MOLECULE_STYLE_LABELS.map((radio, idx) => {
          if (!multipleResidues && isCartoon(radio.value)) return null;
          return (
            <MenuItem type="radio" key={`${idx}-${radio.value}`} value={radio.value} onClick={onClick}>
              {t(radio.label, lang)}
            </MenuItem>
          );
        })}
      </MenuRadioGroup>
    </MainSubMenu>
  );
};

// Sentinel for the "No Overlay" radio choice. The radio framework compares
// values with ===, and a JS `null` is indistinguishable from a missing value
// in that comparison — using an explicit string keeps the selection state
// unambiguous and is translated back to null when writing to the store.
const NO_OVERLAY_VALUE = '__none__';

export const IndividualMoleculeOverlayRadioGroup = () => {
  const testMolecules = useStore(Selector.testMolecules);
  const pickedMoleculeIndex = usePrimitiveStore(Selector.pickedMoleculeIndex);
  const setChanged = usePrimitiveStore(Selector.setChanged);
  const { t } = useTranslation();
  const lang = useLanguage();

  // null = explicitly no overlay; `undefined` CLEARS the field so the molecule
  // follows the lab-wide overlay again (what Undo needs for a molecule that was
  // inheriting — recording it as null removed the lab's overlay from it).
  const setOverlay = (overlay: MolecularViewerStyle | null | undefined) => {
    useStore.getState().set((state) => {
      const mol = state.projectState.testMolecules[pickedMoleculeIndex];
      if (!mol) return;
      if (overlay === undefined) delete mol.overlayStyle;
      else mol.overlayStyle = overlay;
    });
    usePrimitiveStore.getState().updateViewer();
    setChanged(true);
  };

  // See IndividualMoleculeStyleRadioGroup for why the radio value is driven
  // by local state instead of read directly from the store: same dot-not-
  // moving bug with @szhsin/react-menu radios inside the ControlledMenu.
  const stored = testMolecules[pickedMoleculeIndex]?.overlayStyle;
  // `?? NO_OVERLAY_VALUE` covers both undefined (never explicitly set) and
  // null (explicitly no overlay). The radio shows "None" in either case;
  // the renderer separately falls back to the project-level overlay when
  // the field is undefined.
  const storeRadioValue: string = stored ?? NO_OVERLAY_VALUE;
  const [radioValue, setRadioValue] = useState<string>(storeRadioValue);
  useEffect(() => {
    setRadioValue(storeRadioValue);
  }, [storeRadioValue]);

  const onClick = (e: ClickEvent) => {
    e.keepOpen = true;
  };

  return (
    <MainSubMenu label={t('molecularViewer.Overlay', lang)}>
      <MenuRadioGroup
        value={radioValue}
        onRadioChange={(e: RadioChangeEvent) => {
          const raw = e.value as string;
          if (raw === storeRadioValue) return; // re-click of the shown choice: no change, no undo step
          setRadioValue(raw);
          const next: MolecularViewerStyle | null = raw === NO_OVERLAY_VALUE ? null : (raw as MolecularViewerStyle);
          setWithUndo('Select Overlay for Selected Molecule', stored, next, setOverlay);
        }}
      >
        <MenuItem key="overlay-none" type="radio" value={NO_OVERLAY_VALUE} onClick={onClick}>
          {t('molecularViewer.NoOverlay', lang)}
        </MenuItem>
        {OVERLAY_STYLE_LABELS.map((radio, idx) => (
          <MenuItem type="radio" key={`overlay-${idx}-${radio.value}`} value={radio.value} onClick={onClick}>
            {t(radio.label, lang)}
          </MenuItem>
        ))}
      </MenuRadioGroup>
    </MainSubMenu>
  );
};
